import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import Footer from '../unAuth/Components/footer';

function PrivacyPolicy() {
  return (
    <div className="privacy-page">
      <div className="container">
        <Link to="/" className="privacy-back">← Back to Home</Link>
        
        <div className="privacy-content">
          <h1 className="privacy-title">Real World Equity – Privacy Policy</h1>
          <p className="last-updated">Last updated: September 2025</p>
          
          <div className="privacy-text">
            <p>This Privacy Policy explains how Real World Equity, LLC ("RWE," "we," "us," or "our") collects, uses, shares, and protects personal information when you visit our websites (including realworldequity.com), use our services, or otherwise interact with us (collectively, the "Services"). We operate internationally and this Policy includes additional notices for certain regions.</p>
            
            <p><strong>Important consent notice:</strong> When you submit your information through our website lead form (name, phone, email, and any other details you choose to provide), you expressly consent to RWE contacting you by phone call, SMS/text message, and email, including for marketing purposes and using automated technology where permitted by law. Message & data rates may apply. You may opt out at any time (see Your Choices below).</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">1) Information We Collect</h2>
            
            <h3 className="subsection-title">Information you provide</h3>
            <ul>
              <li><strong>Lead form details:</strong> first and last name, phone number, email address, and any other data you submit.</li>
              <li><strong>Communications:</strong> content of emails, calls, and messages with us.</li>
              <li><strong>Account/Onboarding data:</strong> if you create an account or proceed through investor verification (e.g., KYC/AML), we may collect identity and documentation information via our compliance partners.</li>
            </ul>
            
            <h3 className="subsection-title">Information collected automatically</h3>
            <ul>
              <li><strong>Usage data:</strong> pages viewed, links clicked, referring/exit pages, timestamps.</li>
              <li><strong>Device & network data:</strong> IP address, browser, device type, operating system.</li>
              <li><strong>Cookies & similar tech:</strong> pixels, local storage, and SDKs used for site functionality, security, analytics, and (where permitted) marketing. See Cookies & Tracking below.</li>
            </ul>
            
            <h3 className="subsection-title">Information from third parties</h3>
            <ul>
              <li>Vetting & compliance providers (e.g., KYC/AML vendors).</li>
              <li>Analytics/advertising partners (aggregated or pseudonymous insights).</li>
              <li>Public sources (company websites, filings, press).</li>
            </ul>
            
            <p>We process personal data as defined by applicable laws (e.g., GDPR/UK GDPR/CPRA).</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">2) How We Use Your Information</h2>
            
            <p>We use personal information to:</p>
            <ul>
              <li>Respond to inquiries and provide the Services you request.</li>
              <li>Communicate about updates, opportunities, events, and news (email, phone, SMS).</li>
              <li>Perform onboarding & compliance (including identity verification/KYC/AML) through vetted third parties.</li>
              <li>Operate, secure, and improve our sites and Services (including analytics, debugging, and prevention of fraud/abuse).</li>
              <li>Comply with legal obligations and enforce our agreements.</li>
              <li>Market and advertise our offerings where permitted, including measuring campaign performance.</li>
            </ul>
            
            <h3 className="subsection-title">Legal bases (EEA/UK)</h3>
            <p>Where GDPR/UK GDPR applies, our lawful bases include consent, contract, legal obligation, and legitimate interests (e.g., to run and secure our Services, and to inform existing or prospective customers about relevant offerings). You can withdraw consent at any time.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">3) When We Share Information</h2>
            
            <p>We do not sell your personal information. We may share it with:</p>
            <ul>
              <li>Service providers & processors (hosting, cloud, storage, analytics, communications, KYC/AML and compliance, email/SMS platforms, customer support).</li>
              <li>Professional advisors (lawyers, auditors) under confidentiality.</li>
              <li>Authorities when required by law or to protect rights, safety, and security.</li>
              <li>Business transfers (e.g., merger, acquisition, financing, sale of assets).</li>
            </ul>
            
            <p>Where "sharing" for cross-context behavioral advertising is deemed a "sale" or "share" under certain laws (e.g., California), you have the right to opt out (see California Notice).</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">4) International Transfers</h2>
            <p>We operate globally and may transfer, store, or process your information outside your country (including the U.S.). Where required, we use appropriate safeguards (e.g., Standard Contractual Clauses) and take steps to protect your rights.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">5) Data Retention</h2>
            <p>We keep personal information only as long as necessary for the purposes described above, including to satisfy legal, accounting, or reporting requirements, and to maintain business records. We then delete or anonymize it consistent with our retention policy.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">6) Your Choices & Rights</h2>
            
            <h3 className="subsection-title">Marketing choices</h3>
            <ul>
              <li><strong>Email:</strong> click Unsubscribe in any email.</li>
              <li><strong>SMS/Text:</strong> reply STOP to opt out; reply HELP for help.</li>
              <li><strong>Calls:</strong> tell us during a call or email us to opt out of future calls.</li>
            </ul>
            
            <h3 className="subsection-title">Privacy rights</h3>
            <p>Depending on your location, you may have rights to access, correct, delete, restrict, object, data portability, and withdraw consent. To exercise rights, contact us (see Contact Us). We will verify your request consistent with applicable law.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">7) Cookies & Tracking</h2>
            <p>We (and our providers) use cookies and similar technologies for site functionality, analytics, and—where permitted—marketing. You can manage cookies in your browser settings and, where available, via our cookie banner/preferences. Disabling certain cookies may impact site functionality.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">8) Security</h2>
            <p>We implement administrative, technical, and physical safeguards designed to protect personal information (e.g., encryption in transit, access controls). No system is 100% secure; please use strong, unique passwords and notify us of any suspected breach.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">9) Children's Privacy</h2>
            <p>Our Services are not directed to children and we do not knowingly collect personal information from individuals under the age required by applicable law (e.g., under 13 in the U.S., under 16 in parts of the EEA). If you believe a child has provided personal information, contact us and we will take appropriate action.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">10) Regional Notices</h2>
            
            <h3 className="subsection-title">California (CPRA/CCPA)</h3>
            <p>California residents have rights to know/access, delete, correct, opt out of sale/share, and limit use of sensitive personal information. We do not sell personal information for money. We may "share" identifiers and internet/usage data with advertising/analytics partners for cross-context behavioral advertising.</p>
            <p>To exercise rights or opt out: email privacy@realworldequity.com (or use any available web form).</p>
            <p>We will not discriminate for exercising your rights.</p>
            
            <h3 className="subsection-title">EEA/UK</h3>
            <p>For users in the EEA/UK, RWE is the data controller for website leads and communications. You may lodge a complaint with your local supervisory authority, but we encourage contacting us first so we can help.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">11) Third-Party Links</h2>
            <p>Our site may link to third-party websites or services. We are not responsible for their privacy practices. Review their policies before providing personal information.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">12) Changes to This Policy</h2>
            <p>We may update this Policy from time to time. The "Last updated" date indicates the latest revision. Material changes will be highlighted on our website or communicated as required by law.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">13) Contact Us</h2>
            <p><strong>Real World Equity, LLC</strong><br />
            Email: privacy@realworldequity.com<br />
            Mailing Address: [Insert business mailing address]</p>
            <p>If you're in the EEA/UK and wish to contact our EU/UK representative (if and when appointed), please email us for details.</p>
            
            <hr className="section-divider" />
            
            <h2 className="section-title">SMS/Call Consent Disclosure (U.S.)</h2>
            <p>By submitting your phone number in our lead form, you agree that RWE (and/or our service providers) may contact you by calls and texts at the number provided, including using an autodialer or prerecorded voice for informational and marketing purposes. Consent is not a condition of purchase. Message frequency may vary. Message & data rates may apply. Text STOP to opt out; HELP for help.</p>
          </div>
        </div>
        
        <Link to="/" className="privacy-back">← Back to Home</Link>
      </div>
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
